import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { useCurrentOrganization } from '~/auth/use-current-organization'

const ORGANIZATION_QUERY = gql(`
  query OrganizationForBilling($id: ID!) {
    node(id: $id) {
      ... on Organization {
        id
        currentPlan {
          id
          slug
          name
          entitlements
          restrictions
        }
        aiGenerationUsage
        aiModelOptions
        activeSubscriptions {
          edges {
            node {
              id
              status
              billingPlan {
                id
                slug
                name
                entitlements
                restrictions
              }
            }
          }
        }
      }
    }
  }
`)

const RF_PLAN_SLUGS = ['rf_standard', 'rf_premium']

export type AiModelOption = {
  model: string
  config: {
    model: string
    pricing_model: string
  }
  plan_name: string
}

export type AiModelOptions =
  | { mode: 'fixed'; model: string; config: AiModelOption['config']; plan_name: string }
  | { mode: 'choice'; models: AiModelOption[] }
  | null

export function usePlanPermissions(_zoneId?: string) {
  void _zoneId
  const { id: organizationId } = useCurrentOrganization()

  const { data } = useQuery(ORGANIZATION_QUERY, {
    variables: { id: organizationId ?? '' },
    skip: !organizationId,
  })

  const organization = data?.node?.__typename === 'Organization' ? data.node : null
  const currentPlan = organization?.currentPlan
  const restrictions = currentPlan?.restrictions || {}

  const canAccessFeature = useMemo(
    () => (feature: string) => {
      if (!currentPlan) return false
      const features = currentPlan.entitlements?.features || []
      return features.includes(feature)
    },
    [currentPlan]
  )

  const hasRestriction = useMemo(
    () => (restrictionPath: string) => {
      if (!currentPlan) return true // No plan = all restrictions apply
      const parts = restrictionPath.split('.')
      let value = restrictions
      for (const part of parts) {
        value = value?.[part]
        if (value === undefined) return false
      }
      return value === true
    },
    [currentPlan, restrictions]
  )

  const slug = currentPlan?.slug
  const isRoyaltyFree = RF_PLAN_SLUGS.includes(slug ?? '')
  const maxZones = currentPlan?.entitlements?.max_zones ?? 'unlimited'

  // AI model options: determines whether the user must pick a model (mixed plans)
  // or if it's auto-selected (uniform plans).
  const aiModelOptions: AiModelOptions = organization?.aiModelOptions ?? null
  const hasAiModelChoice = aiModelOptions?.mode === 'choice'
  const aiModelOptionsKey = aiModelOptions
    ? aiModelOptions.mode === 'fixed'
      ? `fixed:${aiModelOptions.model}`
      : `choice:${aiModelOptions.models?.map((m) => m.model).join(',')}`
    : 'none'
  const availableAiModels: AiModelOption[] = useMemo(() => {
    if (!aiModelOptions) return []
    if (aiModelOptions.mode === 'fixed') {
      return [{ model: aiModelOptions.model, config: aiModelOptions.config, plan_name: aiModelOptions.plan_name }]
    }
    return aiModelOptions.models ?? []
  }, [aiModelOptionsKey])

  return {
    canAccessFeature,
    hasRestriction,
    currentPlan,
    // Plan type checks
    isRfStandard: slug === 'rf_standard',
    isRfPremium: slug === 'rf_premium',
    isRoyaltyFree,
    isStarter: slug === 'starter',
    isUnlimited: slug === 'unlimited',
    isEnterprise: slug === 'enterprise',
    // Feature convenience checks
    canUseLicensedMusic: !hasRestriction('licensed_music'),
    canGenerateAiMusic: canAccessFeature('ai_generation'),
    maxZones,
    // AI model options — use these to determine whether to show a model picker
    aiModelOptions,
    hasAiModelChoice,
    availableAiModels,
    // AI usage data (RF plans)
    aiGenerationUsage: organization?.aiGenerationUsage,
    organization,
  }
}
